import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, CalendarDays, Clock, MapPin, Upload, CheckCircle2, ShieldAlert,
  IdCard, User, Mail, Phone, CreditCard, Wallet, ArrowRight, ArrowLeft,
  PenLine, Eraser, FileText, Apple, DollarSign, Banknote,
  Copy, ExternalLink, X } from "lucide-react";
import { api } from "../lib/api";
import { TID } from "../lib/testIds";
import { useAuth } from "../context/AuthContext";
import VehicleGallery from "../components/VehicleGallery";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import SmokingFeeToggle from "../components/SmokingFeeToggle";
import CameraCapture from "../components/CameraCapture";
import PoliciesChecklist, { RENTAL_POLICIES } from "../components/PoliciesChecklist";
import VehicleInspectionDiagram from "../components/VehicleInspectionDiagram";
import { useBookingIntent } from "../lib/useBookingIntent";
import { useBlacklistCheck } from "../lib/useBlacklistCheck";

const STEPS = [
  { key: "trip", label: "Trip" },
  { key: "info", label: "Info & Uploads" },
  { key: "contract", label: "Contract" },
  { key: "pay", label: "Payment" },
];

export default function BookRental() {
  const { vehicleId } = useParams();
  const [searchParams] = useSearchParams();
  // Promo / marketing channel from the referring landing page — the URL
  // slug (e.g. `?promo=summer-vip`) is normalised to underscores so the
  // backend stores a consistent tag we can filter/group by later.
  const promoChannel = ((searchParams.get("promo") || "").trim() || null)?.replace(/-/g, "_") || null;
  // Pre-fill from homepage / fleet search bar (ISO minus TZ). We split into
  // date and time so the existing form state (`startDate`, `startTime`, etc.)
  // still drives the pickers.
  const prefillStart = searchParams.get("start") || "";
  const prefillEnd = searchParams.get("end") || "";
  const [pfStartDate, pfStartTime] = prefillStart.split("T");
  const [pfEndDate, pfEndTime] = prefillEnd.split("T");
  const navigate = useNavigate();
  const { user } = useAuth();
  // Walk-in mode is triggered by adding `?walkin=1` to the URL from Back Office.
  // In walk-in mode we call the admin walk-in endpoint (which auto-approves)
  // and show a manager badge.
  const isWalkin = new URLSearchParams(window.location.search).get("walkin") === "1";
  const [vehicle, setVehicle] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    pickup_location: "Plenty Rydez Office — 1818 East New York Ave, Brooklyn NY 11207",
    return_location: "Plenty Rydez Office — 1818 East New York Ave, Brooklyn NY 11207",
    startDate: pfStartDate || "",
    startTime: (pfStartTime || "").slice(0, 5) || "10:00",
    endDate: pfEndDate || "",
    endTime: (pfEndTime || "").slice(0, 5) || "10:00",
    full_name: "",
    email: "",
    phone: "",
    dl_number: "",
    // Extended identity fields (required by the new verification flow)
    date_of_birth: "",
    home_address: "",
    dl_state: "",
    dl_expiration: "",
    accepts_terms: false,
    signature_data: "",
    // Per-page initials for the 4-page rental agreement (typed text, e.g. "JR")
    contract_initials_p1: "",
    contract_initials_p2: "",
    contract_initials_p3: "",
    contract_initials_p4: "",
    // 22-checkbox policy acknowledgement map — seeded empty so nothing is
    // silently pre-accepted. Every entry must be true before submission.
    policies_accepted: {},
    // Customer-facing tap-diagram inspection state
    customer_inspection: { markers: [], acknowledged: false, acknowledged_at: null },
    payment_method: "",
    // When payment_method === "cash", the renter must also choose a channel
    // (Apple Pay / Cash App / Zelle) to send the refundable deposit through.
    deposit_method: "",
    insurance_optin: true,
    has_own_insurance: false,
    own_insurance_carrier: "",
    own_insurance_policy: "",
    // Own-insurance additional fields (per the enhanced verification spec)
    insurance_expiration: "",
    additional_driver_optin: false,
    additional_driver_name: "",
    additional_driver_dl_number: "",
    payment_amount_mode: "full",
    smoking_fee_accepted: false,
  });
  const [dlFront, setDlFront] = useState(null); // primary driver — camera captured
  const [dlBack, setDlBack] = useState(null);   // primary driver — camera captured
  const [selfie, setSelfie] = useState(null);   // live selfie holding DL
  const [addDlFront, setAddDlFront] = useState(null);
  const [addDlBack, setAddDlBack] = useState(null);
  // Own-insurance card image (captured via live camera on the own-insurance block).
  const [insuranceCard, setInsuranceCard] = useState(null);
  const [uploading, setUploading] = useState({ front: false, back: false, addFront: false, addBack: false });
  const [estimate, setEstimate] = useState(null);
  const [availability, setAvailability] = useState(null); // { available, units_free, units_total, message, next_available_at }
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    api.get(`/vehicles/${vehicleId}`).then((r) => setVehicle(r.data)).catch(() => navigate("/fleet"));
  }, [vehicleId, navigate]);

  const [dailyInsuranceRate, setDailyInsuranceRate] = useState(75);
  useEffect(() => {
    api.get("/rentals/insurance-rate")
      .then((r) => setDailyInsuranceRate(Number(r.data?.daily_rate) || 75))
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Consume ?prefill_* params staff sent via "Text & email rental link".
    // Runs once on mount so the renter opens their phone and finds the form
    // already filled with what the manager entered — they only need to add
    // their DL uploads, initials, and signature.
    const p = new URLSearchParams(window.location.search);
    const has = (k) => p.get(k);
    const draftToken = has("draft");
    if (draftToken) {
      // Server-side draft path: fetch the manager-approved snapshot and
      // hydrate the whole form (dates, locations, prices, contact info).
      api.get(`/rental-drafts/${draftToken}`)
        .then((r) => {
          const d = r.data || {};
          const fd = d.form_data || {};
          setForm((f) => ({
            ...f,
            ...fd,
            full_name: fd.full_name || d.full_name || f.full_name,
            email: fd.email || d.email || f.email,
            phone: fd.phone || d.phone || f.phone,
          }));
          toast.success(`Welcome back — ${d.created_by || "our team"} prepared this for you. Add your license and signature to finish.`);
        })
        .catch((e) => {
          toast.error(e?.response?.data?.detail || "This rental link is no longer valid.");
        });
      return;
    }
    setForm((f) => {
      const next = { ...f };
      if (has("prefill_name")) next.full_name = has("prefill_name");
      if (has("prefill_email")) next.email = has("prefill_email");
      if (has("prefill_phone")) next.phone = has("prefill_phone");
      if (has("prefill_pickup")) next.pickup_location = has("prefill_pickup");
      if (has("prefill_return")) next.return_location = has("prefill_return");
      const s = has("prefill_start");
      const e = has("prefill_end");
      if (s) {
        try {
          const d = new Date(s);
          next.startDate = d.toISOString().slice(0, 10);
          next.startTime = d.toISOString().slice(11, 16);
        } catch {}
      }
      if (e) {
        try {
          const d = new Date(e);
          next.endDate = d.toISOString().slice(0, 10);
          next.endTime = d.toISOString().slice(11, 16);
        } catch {}
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only pre-fill from a logged-in user if they are a customer. If a staff
    // member happens to visit this rental page, we do NOT want their info to
    // leak into the renter form (that used to cause "vehicle only driven by
    // Vaughn Louis" style bugs when a manager tried to send a rental through
    // themselves).
    const isStaff = user && (user.role === "staff" || user.role === "admin");
    if (user && !isStaff && !form.full_name) {
      setForm((f) => ({ ...f, full_name: user.name || "", email: user.email || "", phone: user.phone || "" }));
    }
  }, [user]);

  // ---------- Returning-customer autofill ----------
  // Debounced call to /public/customers/prefill when email + phone are both
  // present. If a prior rental matches, silently populate any blank fields so
  // repeat renters don't re-type their address / DL info. We NEVER overwrite
  // fields the user has already typed.
  const [prefillHit, setPrefillHit] = useState(null); // { first_name } | null
  const prefillGuardRef = useRef({ last: "", matched: false });
  useEffect(() => {
    const email = (form.email || "").trim().toLowerCase();
    const phone = (form.phone || "").replace(/\D/g, "");
    if (!email.includes("@") || phone.length < 7) return;
    const key = `${email}|${phone}`;
    if (prefillGuardRef.current.last === key) return;
    prefillGuardRef.current.last = key;
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/public/customers/prefill",
                                 { email: form.email, phone: form.phone });
        if (!r.data?.matched) return;
        const p = r.data.profile || {};
        setForm((f) => {
          const next = { ...f };
          const fill = (k, v) => {
            if (v && (!next[k] || String(next[k]).trim() === "")) next[k] = v;
          };
          fill("full_name",       p.full_name);
          fill("home_address",    p.home_address);
          fill("date_of_birth",   p.date_of_birth);
          fill("dl_number",       p.dl_number);
          fill("dl_state",        p.dl_state);
          fill("dl_expiration",   p.dl_expiration);
          fill("pickup_location", p.pickup_location);
          fill("return_location", p.return_location);
          return next;
        });
        if (!prefillGuardRef.current.matched) {
          prefillGuardRef.current.matched = true;
          toast.success(`Welcome back${p.full_name ? `, ${p.full_name.split(" ")[0]}` : ""} — filled from your last rental.`);
        }
        setPrefillHit({ first_name: (p.full_name || "").split(" ")[0] || "" });
      } catch { /* silent — never block the form */ }
    }, 500);
    return () => clearTimeout(t);
  }, [form.email, form.phone]);

  const isStaffUser = user && (user.role === "staff" || user.role === "admin");

  const startISO = form.startDate ? new Date(`${form.startDate}T${form.startTime}`).toISOString() : "";
  const endISO = form.endDate ? new Date(`${form.endDate}T${form.endTime}`).toISOString() : "";

  // 🕯️ Ghost-pill heartbeat: while the customer is picking dates / filling
  // the form on this vehicle, ping the back office every 10s so managers
  // see a translucent "someone's booking these dates right now" pill on
  // the calendar and don't accidentally take the same reservation on the
  // phone. Automatically stops once the booking is confirmed or the tab
  // closes (useBookingIntent handles the cleanup).
  useBookingIntent({
    enabled: !confirmed && !isStaffUser,
    vehicleId: vehicle?.id,
    vehicleName: vehicle?.name,
    bookingType: "rental",
    startAt: startISO || null,
    endAt: endISO || null,
    stage: ["trip", "renter", "contract", "payment"][step] || null,
  });

  useEffect(() => {
    if (!vehicle || !startISO || !endISO) return;
    api.post("/estimates/rental", {
      vehicle_id: vehicle.id, start_at: startISO, end_at: endISO,
      insurance_optin: form.insurance_optin,
      additional_driver_optin: form.additional_driver_optin,
      smoking_fee_accepted: form.smoking_fee_accepted,
    }).then((r) => setEstimate(r.data)).catch(() => setEstimate(null));
  }, [vehicle, startISO, endISO, form.insurance_optin, form.additional_driver_optin, form.smoking_fee_accepted]);

  // Live availability pill — hits the dedicated rental-availability endpoint.
  useEffect(() => {
    if (!vehicle || !startISO || !endISO) { setAvailability(null); return; }
    let cancelled = false;
    api.post(`/vehicles/${vehicle.id}/rental-availability`, {
      start_at: startISO, end_at: endISO,
    }).then((r) => { if (!cancelled) setAvailability(r.data); })
      .catch(() => { if (!cancelled) setAvailability(null); });
    return () => { cancelled = true; };
  }, [vehicle, startISO, endISO]);

  const uploadDoc = async (side, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [side]: true }));
    const fd = new FormData();
    fd.append("file", file);
    try {
      const kind = (side === "front" || side === "addFront") ? "dl_front" : "dl_back";
      // Do NOT set Content-Type — axios / the browser will attach the proper
      // multipart boundary. Setting it manually strips the boundary and the
      // upload 422s on mobile browsers.
      const { data } = await api.post(`/uploads/document/${kind}`, fd);
      if (side === "front") setDlFront(data);
      else if (side === "back") setDlBack(data);
      else if (side === "addFront") setAddDlFront(data);
      else if (side === "addBack") setAddDlBack(data);
      toast.success(`License ${side} uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [side]: false }));
    }
  };

  // Step guards
  const canProceedTrip = !!(form.startDate && form.endDate && form.pickup_location && form.return_location && estimate && estimate.available !== false && availability?.available !== false);
  const additionalDriverOk = !form.additional_driver_optin || (
    form.additional_driver_name.trim() &&
    form.additional_driver_dl_number.trim() &&
    addDlFront?.file_id && addDlBack?.file_id
  );
  // Staff bypass — managers only assist with data + preview the contract;
  // the renter is responsible for uploading their DL and signing on their own
  // device via the emailed/texted rental link. So we relax the strict field
  // requirements for staff.
  const canProceedInfoStaff = !!(form.full_name.trim() && form.email.trim() && form.phone.trim());
  const canProceedInfoCustomer = !!(
    form.full_name.trim() && form.email.trim() && form.phone.trim() &&
    form.dl_number.trim() && form.date_of_birth && form.home_address.trim() &&
    form.dl_state.trim() && form.dl_expiration &&
    dlFront?.file_id && dlBack?.file_id && selfie?.file_id &&
    additionalDriverOk
  );
  const canProceedInfo = isStaffUser ? canProceedInfoStaff : canProceedInfoCustomer;
  // Early blacklist detection — the moment the renter types their email
  // or phone we hit /public/blacklist-check. If they're barred we hide
  // Continue and show a friendly "please contact us directly" banner so
  // they don't spend 5 minutes filling the rest of the contract in vain.
  const bl = useBlacklistCheck(form.email, form.phone);
  const canProceedInfoGated = canProceedInfo && !bl.blocked;
  const allInitialsOk = !!(
    (form.contract_initials_p1 || "").trim().length >= 2 &&
    (form.contract_initials_p2 || "").trim().length >= 2 &&
    (form.contract_initials_p3 || "").trim().length >= 2 &&
    (form.contract_initials_p4 || "").trim().length >= 2
  );
  const allPoliciesOk = RENTAL_POLICIES.every((p) => !!(form.policies_accepted || {})[p.key]);
  const inspectionOk = !!(form.customer_inspection && form.customer_inspection.acknowledged);
  const canProceedContract = !!(form.accepts_terms && form.signature_data && allInitialsOk && allPoliciesOk && inspectionOk);

  const submitBooking = async (method) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const endpoint = isWalkin ? "/admin/rentals/walkin" : "/bookings/rental";
      const { data: booking } = await api.post(endpoint, {
        vehicle_id: vehicle.id,
        pickup_location: form.pickup_location,
        return_location: form.return_location,
        start_at: startISO, end_at: endISO,
        license_file_id: dlFront.file_id,
        dl_front_file_id: dlFront.file_id,
        dl_back_file_id: dlBack.file_id,
        selfie_with_dl_file_id: selfie?.file_id,
        dl_front_captured_at: dlFront?.captured_at,
        dl_back_captured_at: dlBack?.captured_at,
        selfie_captured_at: selfie?.captured_at,
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        dl_number: form.dl_number,
        date_of_birth: form.date_of_birth,
        home_address: form.home_address,
        dl_state: form.dl_state,
        dl_expiration: form.dl_expiration,
        signature_data: form.signature_data,
        contract_initials_p1: form.contract_initials_p1,
        contract_initials_p2: form.contract_initials_p2,
        contract_initials_p3: form.contract_initials_p3,
        contract_initials_p4: form.contract_initials_p4,
        accepts_terms: true,
        payment_method: method,
        deposit_method: method === "cash" ? (form.deposit_method || null) : null,
        // Marketing tag from the referring landing page's `?promo=…` slug.
        promo_channel: promoChannel || null,
        insurance_optin: form.insurance_optin,
        has_own_insurance: form.has_own_insurance,
        own_insurance_carrier: form.has_own_insurance ? form.own_insurance_carrier : null,
        own_insurance_policy: form.has_own_insurance ? form.own_insurance_policy : null,
        additional_driver_optin: form.additional_driver_optin,
        additional_driver_name: form.additional_driver_optin ? form.additional_driver_name : null,
        additional_driver_dl_number: form.additional_driver_optin ? form.additional_driver_dl_number : null,
        additional_driver_dl_front_file_id: form.additional_driver_optin ? addDlFront?.file_id : null,
        additional_driver_dl_back_file_id: form.additional_driver_optin ? addDlBack?.file_id : null,
        smoking_fee_accepted: form.smoking_fee_accepted,
        policies_accepted: form.policies_accepted,
        customer_inspection: form.customer_inspection,
        insurance_expiration: form.has_own_insurance ? (form.insurance_expiration || null) : null,
        insurance_card_file_id: form.has_own_insurance ? (insuranceCard?.file_id || null) : null,
      });
      const offlineMethods = ["cash", "zelle", "paypal"];
      if (offlineMethods.includes(method)) {
        if (isWalkin) toast.success("Walk-in reservation created & auto-approved.");
        else toast.success("Contract signed. Awaiting manager approval.");
        // Take them to the dedicated confirmation page instead of leaving
        // them on the booking form — no more homepage-dump feeling.
        const share = booking?.share_token
          ? `?token=${encodeURIComponent(booking.share_token)}`
          : "";
        navigate(`/booking/confirmed/${booking.id}${share}`);
        setSubmitting(false);
        return;
      }
      const { data: pay } = await api.post("/payments/checkout", {
        booking_id: booking.id, origin_url: window.location.origin,
        payment_method: ["card", "cashapp", "apple_pay"].includes(form.payment_method) ? form.payment_method : "card",
        amount_mode: form.payment_amount_mode || "full",
      });
      window.location.href = pay.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create rental");
      setSubmitting(false);
    }
  };

  if (!vehicle) {
    return <div className="h-96 flex items-center justify-center">
      <div className="w-10 h-10 rounded-full border-2 border-gold border-t-transparent animate-spin" />
    </div>;
  }

  if (confirmed) {
    const shareUrl = confirmed.share_token
      ? `${window.location.origin}/b/${confirmed.id}?token=${confirmed.share_token}`
      : "";
    const pendingApproval = confirmed.status === "pending_approval";
    return (
      <section data-testid="book-rental-confirmed" className="mx-auto max-w-2xl px-5 sm:px-8 py-16">
        <div className="glass rounded-3xl p-8 sm:p-10 text-center space-y-4">
          <div className="mx-auto w-14 h-14 rounded-full bg-cyan/15 flex items-center justify-center">
            <CheckCircle2 size={28} className="text-cyan" />
          </div>
          <div className="label-caps text-cyan text-xs">
            {pendingApproval ? "Contract signed · pending approval" : "Reservation confirmed"}
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl">Thanks, {form.full_name.split(" ")[0] || "friend"}!</h1>
          <p className="text-white/70 text-sm sm:text-base leading-relaxed">
            {pendingApproval ? (
              <>Your signed rental agreement for the <strong>{vehicle.name}</strong> is now with our team
              for final approval. A confirmation will be sent to <span className="text-white">{form.email}</span> once
              approved (usually within a few hours).</>
            ) : (
              <>Your {vehicle.name} rental is reserved. A Plenty Rydez teammate will complete the
              vehicle inspection and email your finalized contract to <span className="text-white">{form.email}</span>.</>
            )}
          </p>
          <div className="rounded-2xl bg-ink-950/60 border border-white/8 p-4 text-sm text-left inline-block mx-auto space-y-1">
            <div className="flex justify-between gap-8"><span className="text-white/60">Confirmation #</span><span className="text-gold font-mono">{confirmed.id}</span></div>
            <div className="flex justify-between gap-8"><span className="text-white/60">Pickup</span><span>{new Date(confirmed.start_at).toLocaleDateString()}</span></div>
            <div className="flex justify-between gap-8"><span className="text-white/60">Return</span><span>{new Date(confirmed.end_at).toLocaleDateString()}</span></div>
            <div className="flex justify-between gap-8"><span className="text-white/60">Total</span><span className="text-gold">${confirmed.amount?.toFixed(2)}</span></div>
            <div className="flex justify-between gap-8"><span className="text-white/60">Deposit</span><span>${confirmed.deposit?.toFixed(2)}</span></div>
            <div className="flex justify-between gap-8"><span className="text-white/60">Payment</span><span className="uppercase">{confirmed.payment_method}</span></div>
          </div>
          {shareUrl && (
            <div className="rounded-2xl border border-electric/25 bg-electric/5 p-4 space-y-2">
              <div className="label-caps text-electric text-[10px]">Your confirmation link</div>
              <div className="text-[11px] text-white/70 break-all font-mono">{shareUrl}</div>
              <div className="flex gap-2 justify-center pt-1">
                <button
                  onClick={() => {
                    navigator.clipboard?.writeText(shareUrl).then(() => toast.success("Link copied"));
                  }}
                  data-testid="rental-share-copy"
                  className="px-4 py-2 rounded-full text-xs border border-white/15 text-white/80 hover:border-gold hover:text-white inline-flex items-center gap-1.5">
                  <Copy size={12} /> Copy link
                </button>
                <button
                  onClick={() => window.open(shareUrl, "_blank")}
                  data-testid="rental-share-open"
                  className="px-4 py-2 rounded-full text-xs bg-electric text-ink-900 font-semibold hover:brightness-110 inline-flex items-center gap-1.5">
                  <ExternalLink size={12} /> Open confirmation
                </button>
              </div>
              <p className="text-[10px] text-white/40">Save this link — you can revisit anytime to see status, contract, and inspection video.</p>
            </div>
          )}
          <div className="flex gap-3 justify-center pt-2">
            <button
              onClick={() => navigate("/fleet")}
              className="px-5 py-2.5 rounded-full text-xs label-caps border border-white/12 hover:border-white/30">
              Back to fleet
            </button>
            <button
              onClick={() => navigate("/")}
              className="btn-gold px-5 py-2.5 rounded-full text-xs">
              Home
            </button>
          </div>
        </div>
      </section>
    );
  }

  const isRideshareVehicle = vehicle.rideshare_ready;
  const minDays = vehicle.rental_min_days || 2;

  return (
    <section data-testid={TID.bookRental.root} className="mx-auto max-w-7xl px-5 sm:px-8 py-12">
      {prefillHit && (
        <div
          data-testid="rental-prefill-banner"
          className="mb-4 rounded-xl border border-cyan/30 bg-cyan/[0.06] px-4 py-2.5 flex items-start gap-2.5 animate-in fade-in slide-in-from-top-1 duration-300"
        >
          <CheckCircle2 size={14} className="text-cyan mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white">
              We recognized you{prefillHit.first_name ? `, ${prefillHit.first_name}` : ""} — everything below was filled from your last rental.
            </div>
            <div className="text-[11px] text-white/50">
              Double-check every field before you sign. Only your blank fields were filled — anything you already typed was left alone.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setPrefillHit(null)}
            data-testid="rental-prefill-banner-close"
            className="p-1 rounded-full hover:bg-white/10 text-white/40 hover:text-white/80"
            aria-label="Dismiss"
          >
            <X size={13} />
          </button>
        </div>
      )}
      {isWalkin && (
        <div
          data-testid="walkin-banner"
          className="mb-6 rounded-2xl border border-cyan/40 bg-cyan/10 p-4 flex items-center gap-3"
        >
          <div className="w-9 h-9 rounded-lg bg-cyan/20 text-cyan flex items-center justify-center flex-shrink-0">
            <IdCard size={16} />
          </div>
          <div className="flex-1">
            <div className="label-caps text-cyan text-[10px]">Walk-in mode · manager assisted</div>
            <div className="text-sm text-white leading-tight">
              Hand this device to the walk-in customer. Their signed contract will
              be auto-approved on submit (no queue).
            </div>
          </div>
        </div>
      )}

      {isStaffUser && !isWalkin && (
        <StaffSendLinkBanner vehicle={vehicle} form={form} startISO={startISO} endISO={endISO} />
      )}
      <div className="label-caps text-electric mb-2">Self-drive rental</div>
      <h1 className="font-serif text-4xl sm:text-5xl mb-2">{vehicle.name}</h1>
      <p className="text-white/60 mb-6">
        {isRideshareVehicle
          ? "Approved for Uber, Lyft, Uber Eats and DoorDash. Two-day minimum. Valid driver's license required."
          : "Two-day minimum. Valid driver's license required."}
      </p>

      <StepBar current={step} />

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 mt-8">
        <div className="glass rounded-3xl p-6 sm:p-8 space-y-6">
          {step === 0 && (
            <TripStep form={form} setForm={setForm} availability={availability} vehicleId={vehicle?.id} />
          )}
          {step === 1 && (
            <InfoStep
              form={form} setForm={setForm}
              dlFront={dlFront} setDlFront={setDlFront}
              dlBack={dlBack} setDlBack={setDlBack}
              selfie={selfie} setSelfie={setSelfie}
              addDlFront={addDlFront} addDlBack={addDlBack}
              uploading={uploading} uploadDoc={uploadDoc}
              estimate={estimate}
              isStaffUser={isStaffUser}
              blacklist={bl}
            />
          )}
          {step === 2 && (
            <ContractStep form={form} setForm={setForm} vehicle={vehicle} estimate={estimate} isStaffUser={isStaffUser} dailyInsuranceRate={dailyInsuranceRate} insuranceCard={insuranceCard} setInsuranceCard={setInsuranceCard} />
          )}
          {/* Smoking-fee accept/decline lives OUTSIDE the wizard steps so it's
              visible on every step and the customer can flip it late without
              having to jump back to a specific page. */}
          {(step === 1 || step === 2) && (
            <SmokingFeeToggle
              accepted={form.smoking_fee_accepted}
              onChange={(v) => setForm({ ...form, smoking_fee_accepted: v })}
              fee={estimate?.smoking_fee > 0 ? estimate.smoking_fee : 200}
              testId="rental-smoking-fee"
            />
          )}
          {step === 3 && (
            <PaymentStep
              form={form} setForm={setForm}
              estimate={estimate}
              submitting={submitting}
              onSubmit={(m) => submitBooking(m)}
            />
          )}

          <div className="flex items-center justify-between pt-4 border-t border-white/8">
            {step > 0 ? (
              <button
                type="button"
                data-testid={TID.bookRental.stepBack}
                onClick={() => setStep(step - 1)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs label-caps border border-white/12 hover:border-white/30 text-white/70"
              >
                <ArrowLeft size={12} /> Back
              </button>
            ) : <span />}
            {step < 3 && (
              <button
                type="button"
                data-testid={TID.bookRental.stepNext}
                onClick={() => {
                  if (step === 0 && !canProceedTrip) return toast.error("Complete trip dates first");
                  if (step === 1 && bl.blocked) return toast.error("This account can't submit reservations right now. Please contact us directly.");
                  if (step === 1 && !canProceedInfo) {
                    if (isStaffUser) return toast.error("Fill name / email / phone, then send the link to the renter");
                    return toast.error("Complete info + upload both license photos");
                  }
                  if (step === 2 && !canProceedContract) return toast.error("Scroll, sign and accept the contract");
                  setStep(step + 1);
                }}
                className="btn-gold px-5 py-2 rounded-full text-xs inline-flex items-center gap-2"
              >
                Continue <ArrowRight size={12} />
              </button>
            )}
          </div>
        </div>

        <aside data-testid={TID.bookRental.estimate} className="space-y-5">
          <div className="rounded-3xl overflow-hidden border border-white/10">
            <VehicleGallery
              images={vehicle.images}
              image_url={vehicle.image_url}
              alt={vehicle.name}
              aspect="aspect-[16/10]"
              enableLightbox={true}
              testId="book-rental-gallery"
            />
          </div>
          <div className="glass rounded-3xl p-6 space-y-3">
            <div className="label-caps text-electric">Estimate</div>
            <Row k="Requested duration" v={estimate ? `${estimate.requested_days} day(s)` : "—"} />
            <Row k={`Billed (min ${minDays} days)`} v={estimate ? `${estimate.billed_days} day(s)` : "—"} />
            <Row k={`Rate · $${vehicle.daily_rate}/day`} v={estimate ? `$${estimate.subtotal?.toFixed(2)}` : "—"} />
            {estimate?.additional_driver_optin && (
              <Row k={<span className="text-gold">Additional driver · $10/day</span>}
                   v={<span className="text-gold">${estimate.additional_driver_total?.toFixed(2)}</span>} />
            )}
            {estimate?.insurance_optin && (
              <Row k={<span className="text-gold">Insurance · ${dailyInsuranceRate.toFixed(2)}/day × {estimate.billed_days || 0} day(s)</span>}
                   v={<span className="text-gold">${estimate.insurance_total?.toFixed(2)}</span>} />
            )}
            {estimate?.smoking_fee > 0 && (
              <Row k={<span className="text-amber-300">Smoking permitted fee</span>}
                   v={<span className="text-amber-300" data-testid="rental-estimate-smoking-fee">${estimate.smoking_fee.toFixed(2)}</span>} />
            )}
            <Row k={estimate?.tax_rate === 0 ? "Sales tax (waived)" : "Sales tax (8.875%)"}
                 v={estimate ? `$${estimate.tax?.toFixed(2)}` : "—"} />
            <div className="border-t border-white/10 pt-3" />
            <Row k={<span className="text-white">Charged today</span>}
                 v={<span data-testid={TID.bookRental.total} className="text-gold text-lg">${estimate?.total?.toFixed(2) || "—"}</span>} />
            <Row k="Refundable deposit at pickup" v={estimate ? `$${estimate.deposit?.toFixed(2)}` : "—"} />
            {vehicle.daily_mileage_limit != null && (
              <Row k="Daily mileage limit" v={<span className="text-electric">{vehicle.daily_mileage_limit} mi/day</span>} />
            )}
            {estimate && estimate.available === false && (
              <div className="text-xs text-red-400 pt-2 flex items-center gap-1.5">
                <ShieldAlert size={12} /> Vehicle unavailable for the selected window.
              </div>
            )}
            {estimate && estimate.billed_days > estimate.requested_days && (
              <div className="text-xs text-electric pt-2">
                Below the {minDays}-day minimum — billed at {estimate.billed_days} days.
              </div>
            )}
            {isRideshareVehicle && (
              <div className="mt-3 rounded-xl border border-gold/25 bg-gold/5 p-3">
                <div className="label-caps text-gold mb-1">Rideshare & Delivery Approved</div>
                <p className="text-xs text-white/70">
                  This vehicle is cleared for Uber, Lyft, Uber Eats & DoorDash. Bring platform docs & license.
                </p>
              </div>
            )}
          </div>
        </aside>
      </div>
      <StyleTag />
    </section>
  );
}

/* ---------- Step 1: Trip ---------- */
function TripStep({ form, setForm, availability, vehicleId }) {
  const showPill = form.startDate && form.endDate && availability;
  const pillTone = availability?.available
    ? (availability.units_free < availability.units_total ? "amber" : "cyan")
    : "red";
  const pillClasses = {
    cyan:  "bg-cyan/15 border-cyan/40 text-cyan",
    amber: "bg-amber-400/15 border-amber-400/40 text-amber-300",
    red:   "bg-red-500/15 border-red-500/40 text-red-300",
  }[pillTone];
  return (
    <div className="space-y-5">
      <div className="label-caps text-electric text-xs">Step 1 · Trip details</div>

      {showPill && (
        <div
          data-testid="rental-availability-pill"
          className={`rounded-2xl border p-3 flex items-center gap-3 ${pillClasses}`}
        >
          <div className={`w-2.5 h-2.5 rounded-full ${
            availability.available ? "bg-current animate-pulse" : "bg-current"
          }`} />
          <div className="flex-1 text-sm leading-tight">
            <div className="font-semibold">{availability.message}</div>
            {!availability.available && availability.next_available_at && (
              <div className="text-xs opacity-80 mt-0.5">
                Next free: {new Date(availability.next_available_at).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      )}

      <FieldRow label="Pickup location" icon={MapPin}>
        <input
          data-testid={TID.bookRental.pickup}
          value={form.pickup_location}
          onChange={(e) => setForm({ ...form, pickup_location: e.target.value })}
          className="input"
          required
        />
      </FieldRow>
      <FieldRow label="Return location" icon={MapPin}>
        <input
          data-testid={TID.bookRental.return}
          value={form.return_location}
          onChange={(e) => setForm({ ...form, return_location: e.target.value })}
          className="input"
          required
        />
      </FieldRow>
      <AvailabilityCalendar
        vehicleId={vehicleId}
        value={{ start: form.startDate || null, end: form.endDate || null }}
        onChange={({ start, end, suggestion }) => {
          setForm({ ...form, startDate: start || "", endDate: end || "" });
          if (suggestion) {
            toast.info(
              `Those dates aren't available — nearest match is ${suggestion.start} → ${suggestion.end}`,
              { duration: 6000 }
            );
          }
        }}
      />
      <div className="grid grid-cols-2 gap-4">
        <FieldRow label="Start date" icon={Calendar}>
          <input type="date"
            data-testid={`${TID.bookRental.startAt}-date`}
            value={form.startDate}
            min={new Date().toISOString().split("T")[0]}
            onChange={(e) => setForm({ ...form, startDate: e.target.value })}
            className="input" required />
        </FieldRow>
        <FieldRow label="Start time" icon={Clock}>
          <input type="time"
            data-testid={`${TID.bookRental.startAt}-time`}
            value={form.startTime}
            onChange={(e) => setForm({ ...form, startTime: e.target.value })}
            className="input" required />
        </FieldRow>
        <FieldRow label="Return date" icon={Calendar}>
          <input type="date"
            data-testid={`${TID.bookRental.endAt}-date`}
            value={form.endDate}
            min={form.startDate || new Date().toISOString().split("T")[0]}
            onChange={(e) => setForm({ ...form, endDate: e.target.value })}
            className="input" required />
        </FieldRow>
        <FieldRow label="Return time" icon={Clock}>
          <input type="time"
            data-testid={`${TID.bookRental.endAt}-time`}
            value={form.endTime}
            onChange={(e) => setForm({ ...form, endTime: e.target.value })}
            className="input" required />
        </FieldRow>
      </div>
    </div>
  );
}

/* ---------- Step 2: Info + Live Identity Verification ---------- */
function InfoStep({
  form, setForm,
  dlFront, setDlFront, dlBack, setDlBack, selfie, setSelfie,
  addDlFront, addDlBack, uploading, uploadDoc, estimate, isStaffUser = false,
  blacklist = { blocked: false, reason: null, checking: false },
}) {
  const addFrontRef = useRef(null);
  const addBackRef = useRef(null);
  const days = estimate?.days || 0;
  return (
    <div className="space-y-5">
      <div className="label-caps text-electric text-xs">Step 2 · Identity verification &amp; driver&apos;s license</div>

      {/* Early blacklist banner — hits /public/blacklist-check as they type
          email / phone so a barred client sees this before wasting time
          filling the whole 30-field contract. */}
      {blacklist.blocked && (
        <div
          data-testid="rental-blacklist-notice"
          className="rounded-2xl border border-red-500/50 bg-red-500/[0.08] p-4 text-[12px] leading-relaxed text-red-100"
        >
          <div className="label-caps text-red-300 text-[10px] mb-1.5">Reservation not accepted</div>
          <div className="text-white/90 mb-1">
            This email / phone number is unable to submit a reservation right now.
          </div>
          {blacklist.reason && (
            <div className="mt-2 rounded-lg bg-black/25 border border-red-500/25 px-3 py-2 text-white/85">
              <span className="label-caps text-[9px] text-red-300">Reason&nbsp;</span>
              {blacklist.reason}
            </div>
          )}
          <div className="text-white/60 text-[11px] mt-2">
            If you believe this is a mistake, please contact us directly at
            <span className="text-white"> pay@plenty-rydez.com</span> or <span className="text-white">929-899-3467</span>.
          </div>
        </div>
      )}

      {/* Customer notice — legal / fraud-prevention disclosure */}
      <div data-testid="rental-identity-notice"
        className="rounded-2xl border border-amber-400/40 bg-amber-400/[0.06] p-4 text-[12px] leading-relaxed text-amber-100">
        <div className="label-caps text-amber-300 text-[10px] mb-1.5">For your protection &amp; ours</div>
        Plenty Rydez requires <strong>live camera capture</strong> of your driver&apos;s license (front and back) and a
        <strong> live selfie holding your driver&apos;s license next to your face</strong>. Existing photos from your
        gallery cannot be uploaded. This verification helps prevent fraud and unauthorized vehicle rentals.
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <FieldRow label="Full legal name" icon={User}>
          <input data-testid={TID.bookRental.fullName}
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="input" required />
        </FieldRow>
        <FieldRow label="Date of birth" icon={CalendarDays}>
          <input type="date"
            data-testid="rental-date-of-birth"
            value={form.date_of_birth}
            onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
            max={new Date(Date.now() - 21*365*86400000).toISOString().slice(0,10)}
            className="input" required />
        </FieldRow>
        <FieldRow label="Email" icon={Mail}>
          <input data-testid={TID.bookRental.email} type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="input" required />
        </FieldRow>
        <FieldRow label="Phone" icon={Phone}>
          <input data-testid={TID.bookRental.phone} type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="input" required />
        </FieldRow>
      </div>

      <FieldRow label="Home address" icon={MapPin}>
        <input
          data-testid="rental-home-address"
          value={form.home_address}
          onChange={(e) => setForm({ ...form, home_address: e.target.value })}
          placeholder="123 Main St, Apt 4B, Brooklyn, NY 11201"
          className="input" required />
      </FieldRow>

      <div className="grid sm:grid-cols-3 gap-4">
        <FieldRow label="Driver's license #" icon={IdCard}>
          <input data-testid={TID.bookRental.dlNumber}
            value={form.dl_number}
            onChange={(e) => setForm({ ...form, dl_number: e.target.value })}
            className="input" required />
        </FieldRow>
        <FieldRow label="State issued" icon={IdCard}>
          <input
            data-testid="rental-dl-state"
            value={form.dl_state}
            onChange={(e) => setForm({ ...form, dl_state: e.target.value.toUpperCase().slice(0,2) })}
            placeholder="NY"
            maxLength={2}
            className="input uppercase" required />
        </FieldRow>
        <FieldRow label="Expiration date" icon={CalendarDays}>
          <input type="date"
            data-testid="rental-dl-expiration"
            value={form.dl_expiration}
            onChange={(e) => setForm({ ...form, dl_expiration: e.target.value })}
            min={new Date().toISOString().slice(0,10)}
            className="input" required />
        </FieldRow>
      </div>

      {/* Live camera-only capture flow — no gallery uploads permitted */}
      {isStaffUser ? (
        <div data-testid="rental-identity-locked"
          className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-[12px] text-white/60">
          Identity verification (DL front, DL back, and live selfie) is completed by the renter on their own device
          via the texted rental link. Staff cannot capture on behalf of the customer.
        </div>
      ) : (
        <div className="space-y-3">
          <div className="label-caps text-cyan text-[10px]">Live identity capture · all three required</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <CameraCapture
              label="1 · License FRONT"
              facingMode="environment"
              uploadKind="dl_front"
              captured={dlFront}
              onCaptured={setDlFront}
              overlayHint="Hold license flat, fill the frame"
              testId="rental-cam-dl-front"
            />
            <CameraCapture
              label="2 · License BACK"
              facingMode="environment"
              uploadKind="dl_back"
              captured={dlBack}
              onCaptured={setDlBack}
              overlayHint="Barcode / signature side"
              testId="rental-cam-dl-back"
            />
            <CameraCapture
              label="3 · Selfie holding license"
              facingMode="user"
              uploadKind="selfie_with_dl"
              captured={selfie}
              onCaptured={setSelfie}
              overlayHint="Both your face and license visible"
              testId="rental-cam-selfie"
            />
          </div>
          <p className="text-[11px] text-white/50">
            Photos are captured live from your camera, encrypted, and stored securely. Only Plenty Rydez staff can view
            them for identity verification. Uploading existing images from your photo gallery is not permitted.
          </p>
        </div>
      )}

      {/* Additional driver toggle & panel */}
      <div className={`rounded-2xl border p-4 sm:p-5 ${
        form.additional_driver_optin
          ? "border-gold/40 bg-gold/5"
          : "border-white/12 bg-white/[.02]"
      }`}>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="rental-additional-driver-toggle"
            checked={form.additional_driver_optin}
            onChange={(e) => setForm({ ...form, additional_driver_optin: e.target.checked })}
            className="mt-1 accent-gold w-4 h-4"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-serif text-base">Add an additional driver</div>
              <span className="label-caps text-[10px] px-2 py-0.5 rounded-full bg-gold/15 text-gold border border-gold/30">
                +$10/day
              </span>
            </div>
            <div className="text-xs text-white/60 mt-1">
              Anyone else who will drive the vehicle must be named on the contract. Only the person(s) named here are authorized — no exceptions.
              {days > 0 && form.additional_driver_optin && (
                <span className="block text-gold mt-1">
                  Additional driver: <strong>${(10 * days).toFixed(2)}</strong> for {days} day(s).
                </span>
              )}
            </div>
          </div>
        </label>

        {form.additional_driver_optin && (
          <div className="mt-4 pt-4 border-t border-white/8 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <FieldRow label="Additional driver — full name" icon={User}>
                <input
                  data-testid="rental-additional-driver-name"
                  value={form.additional_driver_name}
                  onChange={(e) => setForm({ ...form, additional_driver_name: e.target.value })}
                  className="input" required
                />
              </FieldRow>
              <FieldRow label="Their driver's license #" icon={IdCard}>
                <input
                  data-testid="rental-additional-driver-dl-number"
                  value={form.additional_driver_dl_number}
                  onChange={(e) => setForm({ ...form, additional_driver_dl_number: e.target.value })}
                  className="input" required
                />
              </FieldRow>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <DLUploadCard
                label="Their License FRONT"
                side="front"
                data={addDlFront}
                busy={uploading.addFront}
                inputRef={addFrontRef}
                testId="rental-additional-driver-dl-front-input"
                statusTestId="rental-additional-driver-dl-front-status"
                onFile={(f) => uploadDoc("addFront", f)}
              />
              <DLUploadCard
                label="Their License BACK"
                side="back"
                data={addDlBack}
                busy={uploading.addBack}
                inputRef={addBackRef}
                testId="rental-additional-driver-dl-back-input"
                statusTestId="rental-additional-driver-dl-back-status"
                onFile={(f) => uploadDoc("addBack", f)}
              />
            </div>
            <p className="text-[11px] text-white/50">
              The additional driver&apos;s photos and license number are stored the same way as the primary driver&apos;s — only Plenty Rydez staff can view them.
            </p>
          </div>
        )}
        <SmokingFeeToggle
          value={form.smoking_fee_accepted}
          onChange={(v) => setForm({ ...form, smoking_fee_accepted: v })}
        />
      </div>
    </div>
  );
}

function DLUploadCard({ label, side, data, busy, inputRef, testId, statusTestId, onFile }) {
  return (
    <div className="rounded-2xl border border-electric/25 bg-electric/5 p-5">
      <div className="flex items-center gap-2 mb-3">
        <IdCard size={16} className="text-electric" />
        <div className="label-caps text-electric text-xs">{label}</div>
      </div>
      <input
        ref={inputRef}
        data-testid={testId}
        type="file"
        accept="image/*,application/pdf"
        onChange={(e) => onFile(e.target.files?.[0])}
        className="hidden"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="btn-outline-electric px-4 py-2 rounded-full text-xs inline-flex items-center gap-2 hover:text-white disabled:opacity-50"
      >
        <Upload size={12} />
        {busy ? "Uploading…" : data?.file_id ? "Replace" : `Upload ${side}`}
      </button>
      <div data-testid={statusTestId} className="text-xs mt-3">
        {data?.file_id ? (
          <span className="text-electric flex items-center gap-1.5">
            <CheckCircle2 size={12} /> {data.original_filename || "on file"}
          </span>
        ) : (
          <span className="text-white/50 flex items-center gap-1.5">
            <ShieldAlert size={12} /> Not uploaded yet
          </span>
        )}
      </div>
    </div>
  );
}

/* ---------- Step 3: Contract + Signature ---------- */
function ContractStep({ form, setForm, vehicle, estimate, isStaffUser = false, dailyInsuranceRate = 75, insuranceCard = null, setInsuranceCard = () => {} }) {
  const [page, setPage] = useState(1);
  const totalPages = 4;
  const pageInitial = (n) => form[`contract_initials_p${n}`];
  const setPageInitial = (n, v) => setForm((f) => ({ ...f, [`contract_initials_p${n}`]: v }));
  // Enforce a 2-character minimum on each page's initials to prevent
  // accidental / joke single-letter entries (e.g. "A" alone is not valid).
  // Staff users cannot initial themselves — we let them page through so they
  // can review the contract with the renter on a call, then send the link.
  const pageInitialsValid = (n) => isStaffUser || (pageInitial(n) || "").trim().length >= 2;
  const allInitialed = isStaffUser || [1, 2, 3, 4].every((n) => (pageInitial(n) || "").trim().length >= 2);
  const today = new Date().toLocaleDateString();

  return (
    <div className="space-y-5">
      <div className="label-caps text-electric text-xs">Step 3 · Rental agreement</div>

      {/* Progress dots */}
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4].map((n) => {
          const done = pageInitialsValid(n);
          const active = n === page;
          return (
            <button
              key={n}
              type="button"
              onClick={() => setPage(n)}
              data-testid={`contract-page-dot-${n}`}
              className={`flex-1 h-1.5 rounded-full transition-all ${
                done ? "bg-cyan" : active ? "bg-cyan/50" : "bg-white/10"
              }`}
              aria-label={`Go to page ${n}`}
            />
          );
        })}
      </div>

      {page === 1 && (
        <ContractPage n={1} title="Website Rental Terms & Conditions">
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3">
            <div className="label-caps text-red-300 text-[10px] mb-1">Authorized driver — critical</div>
            <p className="text-white/85 text-xs">
              This vehicle is <strong>ONLY</strong> to be driven by
              <span className="text-cyan"> {form.full_name || "the Renter"}</span>.
              Unauthorized driver use = immediate vehicle shutdown + total deposit forfeiture.
            </p>
          </div>
          <p><strong>Customer Acknowledgment.</strong> By proceeding with a reservation through Plenty Rydez LLC, you acknowledge that you have read, understood, and agree to these Rental Terms and Conditions. This agreement is intended for online acceptance and forms a legally binding contract upon electronic acceptance.</p>
          <p><strong>Eligibility.</strong> The renter must meet all age, licensing, identity verification, payment, and insurance requirements established by Plenty Rydez LLC.</p>
          <p><strong>Reservations.</strong> Reservations are requests only until approved by Plenty Rydez LLC after payment authorization and availability verification.</p>
          <p><strong>Rental Fees.</strong> The renter agrees to pay all rental charges, taxes, deposits, tolls, citations, administrative fees, damages, fuel, cleaning, late fees, and collection costs.
            {estimate?.deposit != null && (
              <span className="block mt-1 text-cyan">
                Security deposit for this booking: <strong>${estimate.deposit.toFixed(2)}</strong>.
              </span>
            )}
          </p>
          <InitialsBox n={1} value={pageInitial(1)} onChange={(v) => setPageInitial(1, v)} staffLocked={isStaffUser} />
        </ContractPage>
      )}

      {page === 2 && (
        <ContractPage n={2} title="Deposit · Drivers · Insurance">
          <p><strong>Security Deposit.</strong> Security deposits may be applied to unpaid balances, damages, excessive cleaning, smoking, fuel shortages, tolls, or other contractual obligations. Deposits can take up to 72 hours to be refunded after inspection.</p>
          <p><strong>Authorized Drivers.</strong> Only drivers approved by Plenty Rydez LLC may operate the vehicle. Each additional named driver is <strong>$10/day</strong> and must present a valid driver&apos;s license and be at least 25 years of age.</p>
          <p><strong>Insurance &amp; Liability.</strong> The renter accepts full responsibility for all losses not covered by applicable insurance. Renters without personal auto coverage may elect our supplemental package at <strong>${dailyInsuranceRate.toFixed(2)}/day</strong> (billed per rental day).</p>
          <InitialsBox n={2} value={pageInitial(2)} onChange={(v) => setPageInitial(2, v)} staffLocked={isStaffUser} />
        </ContractPage>
      )}

      {page === 3 && (
        <ContractPage n={3} title="Vehicle Care · Prohibited Use · GPS">
          <p><strong>Vehicle Care.</strong> The vehicle shall be returned in substantially the same condition received, excluding normal wear. Full tank in, full tank out — unreturned fuel is billed at $9.99/gallon.</p>
          <p><strong>Prohibited Use.</strong> No racing, reckless driving, impaired driving, illegal activity, unauthorized drivers, or off-road use unless approved. Rideshare & commercial delivery are prohibited unless the vehicle is expressly rideshare-approved.</p>
          <p><strong>GPS &amp; Recovery.</strong> Vehicles may utilize GPS, telematics, and remote disabling technology for theft prevention and recovery.</p>
          <InitialsBox n={3} value={pageInitial(3)} onChange={(v) => setPageInitial(3, v)} staffLocked={isStaffUser} />
        </ContractPage>
      )}

      {page === 4 && (
        <ContractPage n={4} title="Accidents · Default · Electronic Acceptance">
          <p><strong>Accidents &amp; Violations.</strong> The renter is responsible for promptly reporting accidents and paying all tolls, citations, parking violations, and associated fees.</p>
          <p><strong>Default &amp; Repossession.</strong> Failure to comply with this agreement may result in immediate repossession and additional charges. Any chargebacks, non-payment, or repossessions cause total deposit forfeiture.</p>
          <p><strong>Electronic Acceptance.</strong> Checking &quot;I Agree&quot; and drawing your signature below constitute your legally binding electronic signature.</p>

          {/* Insurance selection — client can use their own OR opt into Plenty Rydez coverage.
              Plenty Rydez insurance is auto-selected by default so customers without personal
              coverage are always insured. */}
          <div className="rounded-xl border border-white/10 p-3 mt-3 space-y-2">
            <div className="label-caps text-cyan text-[10px]">Insurance coverage *</div>
            <label className={`flex items-start gap-3 rounded-lg p-2.5 cursor-pointer border transition ${
              !form.has_own_insurance ? "border-cyan/40 bg-cyan/5" : "border-white/8"
            }`}>
              <input type="radio" name="ins" data-testid="rental-ins-plenty"
                checked={!form.has_own_insurance}
                onChange={() => setForm({ ...form, has_own_insurance: false, insurance_optin: true })}
                className="mt-1 accent-cyan w-4 h-4" />
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-white">Add Plenty Rydez insurance</span>
                  <span className="label-caps text-[10px] px-2 py-0.5 rounded-full bg-cyan/15 text-cyan border border-cyan/30">
                    ${dailyInsuranceRate.toFixed(2)}/day
                  </span>
                  <span className="label-caps text-[9px] text-lime-300">Recommended</span>
                </div>
                <div className="text-[11px] text-white/60 mt-1">
                  Full supplemental coverage for the length of your rental. Auto-added if you don't have your own policy.
                  {estimate?.insurance_optin && estimate.insurance_total > 0 && (
                    <span className="block text-cyan mt-1">
                      Total for this rental: <strong>${estimate.insurance_total.toFixed(2)}</strong>.
                    </span>
                  )}
                </div>
              </div>
            </label>
            <label className={`flex items-start gap-3 rounded-lg p-2.5 cursor-pointer border transition ${
              form.has_own_insurance ? "border-cyan/40 bg-cyan/5" : "border-white/8"
            }`}>
              <input type="radio" name="ins" data-testid="rental-ins-own"
                checked={form.has_own_insurance}
                onChange={() => setForm({ ...form, has_own_insurance: true, insurance_optin: false })}
                className="mt-1 accent-cyan w-4 h-4" />
              <div className="flex-1">
                <div className="font-medium text-sm text-white">I'll use my own insurance</div>
                <div className="text-[11px] text-white/60 mt-1">Proof of personal auto coverage required at pickup.</div>
                {form.has_own_insurance && (
                  <div className="space-y-2 mt-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input data-testid="rental-own-ins-carrier"
                        placeholder="Carrier (e.g. Geico)"
                        value={form.own_insurance_carrier}
                        onChange={(e) => setForm({ ...form, own_insurance_carrier: e.target.value })}
                        className="bg-ink-950/70 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30" />
                      <input data-testid="rental-own-ins-policy"
                        placeholder="Policy #"
                        value={form.own_insurance_policy}
                        onChange={(e) => setForm({ ...form, own_insurance_policy: e.target.value })}
                        className="bg-ink-950/70 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30" />
                    </div>
                    <div>
                      <label className="block">
                        <span className="label-caps text-[10px] text-white/50 block mb-1">Policy expiration date</span>
                        <input type="date"
                          data-testid="rental-own-ins-expiration"
                          value={form.insurance_expiration}
                          onChange={(e) => setForm({ ...form, insurance_expiration: e.target.value })}
                          min={new Date().toISOString().slice(0,10)}
                          className="input w-full" />
                      </label>
                    </div>
                    <div className="text-[11px] text-white/60">
                      Must include collision &amp; comprehensive coverage. Capture your insurance card below —
                      if you decline, Plenty Rydez insurance will be added instead.
                    </div>
                    <CameraCapture
                      label="Insurance card (front)"
                      facingMode="environment"
                      uploadKind="insurance"
                      captured={insuranceCard}
                      onCaptured={setInsuranceCard}
                      overlayHint="Show carrier, policy #, expiration"
                      testId="rental-cam-insurance-card"
                    />
                  </div>
                )}
              </div>
            </label>
          </div>

          {/* Printed name + date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <label className="block">
              <span className="label-caps text-[10px] text-white/50 block mb-1">Printed name</span>
              <input
                data-testid="contract-printed-name"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="input w-full"
                placeholder="Full legal name"
              />
            </label>
            <label className="block">
              <span className="label-caps text-[10px] text-white/50 block mb-1">Date</span>
              <input
                readOnly
                value={today}
                className="input w-full opacity-70 cursor-default"
              />
            </label>
          </div>

          {/* Signature pad */}
          <div className="mt-4">
            <SignaturePad
              value={form.signature_data}
              onChange={(dataUrl) => setForm((f) => ({ ...f, signature_data: dataUrl }))}
              staffLocked={isStaffUser}
            />
          </div>

          <InitialsBox n={4} value={pageInitial(4)} onChange={(v) => setPageInitial(4, v)} staffLocked={isStaffUser} />

          {/* 22-checkbox policy acknowledgement — legally binding rules the
              customer must individually acknowledge before signing. */}
          {!isStaffUser && (
            <div className="mt-4 space-y-4">
              <PoliciesChecklist
                value={form.policies_accepted || {}}
                onChange={(next) => setForm({ ...form, policies_accepted: next })}
                testId="rental-policies-checklist"
              />
              <VehicleInspectionDiagram
                value={form.customer_inspection}
                onChange={(next) => setForm({ ...form, customer_inspection: next })}
                testId="rental-vehicle-inspection"
              />
            </div>
          )}

          {/* Final agree */}
          <label className="flex items-start gap-3 cursor-pointer mt-4 p-3 rounded-xl border border-cyan/30 bg-cyan/5">
            <input
              data-testid={TID.bookRental.accept}
              type="checkbox"
              checked={form.accepts_terms}
              onChange={(e) => setForm({ ...form, accepts_terms: e.target.checked })}
              className="mt-0.5 accent-cyan w-5 h-5 flex-shrink-0"
              disabled={!allInitialed || !form.signature_data}
            />
            <span className="text-sm text-white leading-snug">
              <strong>I have read and agree</strong> to the Plenty Rydez Website Rental Terms &amp; Conditions.
              My typed initials on every page and my signature above constitute a legally binding electronic signature.
            </span>
          </label>
          {(!allInitialed || !form.signature_data) && (
            <p className="text-xs text-amber-300 mt-2">
              {!allInitialed && "Initial every page (2+ letters each) before agreeing. "}
              {!form.signature_data && "Draw your signature above."}
            </p>
          )}
        </ContractPage>
      )}

      {/* Page nav */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
          data-testid="contract-prev-page"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs label-caps border border-white/12 text-white/70 hover:border-white/30 disabled:opacity-30"
        >
          <ArrowLeft size={12} /> Prev page
        </button>
        {page < totalPages ? (
          <button
            type="button"
            disabled={!pageInitialsValid(page)}
            onClick={() => setPage(page + 1)}
            data-testid="contract-next-page"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs label-caps bg-cyan text-midnight font-semibold hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isStaffUser ? "Review · continue" : "Initial & continue"} <ArrowRight size={12} />
          </button>
        ) : isStaffUser ? (
          <div className="text-xs text-amber-300 text-right leading-tight">
            Contract review complete.<br />
            Scroll up and use <span className="text-cyan">"Text &amp; email rental link"</span> to send it to the renter.
          </div>
        ) : (
          <div className="text-xs text-white/60">
            {allInitialed && form.signature_data && form.accepts_terms
              ? "Ready · tap Continue below to pay"
              : "Complete this page to finish"}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Stable page wrapper — MUST be defined at module scope, not inside ContractStep,
 * or React will unmount its children on every parent re-render (which is what
 * caused initials + signature to appear blank on mobile).
 */
function ContractPage({ n, title, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-ink-950/70 p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div className="label-caps text-cyan text-[10px]">Page {n} of 4</div>
        <div className="text-[10px] text-white/40 label-caps">Plenty Rydez LLC</div>
      </div>
      <div className="font-serif text-lg text-white">{title}</div>
      <div className="text-sm text-white/75 leading-relaxed space-y-3">{children}</div>
    </div>
  );
}

function InitialsBox({ n, value, onChange, staffLocked = false }) {
  // Allow only letters + periods; cap at 5. Trim before validating.
  const clean = (s) => s.replace(/[^A-Za-z\.]/g, "").slice(0, 5).toUpperCase();
  const trimmed = (value || "").trim();
  const valid = trimmed.length >= 2;
  const tooShort = trimmed.length === 1;
  if (staffLocked) {
    return (
      <div className="mt-3 flex items-center gap-3 p-3 rounded-xl border-2 border-dashed border-amber-400/50 bg-amber-400/[0.06]">
        <PenLine size={16} className="text-amber-300 flex-shrink-0" />
        <div className="flex-1">
          <div className="label-caps text-amber-300 text-[10px]">Locked · renter-only</div>
          <div className="text-xs text-white/75 mt-0.5">
            The renter must initial page {n} on their own device — this cannot be done by staff.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      className={`mt-3 flex items-center gap-3 p-3 rounded-xl border-2 border-dashed bg-cyan/5 transition-colors ${
        valid ? "border-cyan" : tooShort ? "border-amber-400/60" : "border-cyan/40"
      }`}
    >
      <PenLine size={16} className="text-cyan flex-shrink-0" />
      <label className="flex-1 flex items-center gap-2 flex-wrap">
        <span className="text-xs text-white/70">Type your initials to accept page {n}:</span>
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(clean(e.target.value))}
          data-testid={`contract-initials-p${n}`}
          placeholder="JR"
          maxLength={5}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          style={{ background: "#000", color: "#FFFFFF" }}
          className="font-mono text-xl font-bold tracking-widest border-2 border-cyan focus:outline-none focus:ring-2 focus:ring-cyan/50 rounded-lg px-4 py-2.5 w-28 text-center uppercase placeholder:text-white/25"
        />
        {valid ? (
          <span className="text-[10px] text-cyan label-caps">✓ Initialed</span>
        ) : tooShort ? (
          <span className="text-[10px] text-amber-300 label-caps">Need 2+ letters</span>
        ) : (
          <span className="text-[10px] text-white/40 label-caps">2 letters minimum</span>
        )}
      </label>
    </div>
  );
}

function SignaturePad({ disabled = false, staffLocked = false, value, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(!!value);
  const isDisabled = disabled || staffLocked;

  // Paint the canvas solid black on mount so drawn strokes are guaranteed visible.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, c.width, c.height);
  }, []);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (c.width / rect.width),
             y: (t.clientY - rect.top) * (c.height / rect.height) };
  };
  const start = (e) => {
    if (isDisabled) return;
    e.preventDefault();
    drawing.current = true;
    last.current = getPos(e);
  };
  const move = (e) => {
    if (!drawing.current || isDisabled) return;
    e.preventDefault();
    const p = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = 3.5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    setHasInk(true);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onChange(dataUrl);
  };

  const clear = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false);
    onChange("");
  }, [onChange]);

  if (staffLocked) {
    // Show a locked placeholder instead of the canvas so managers can't sign.
    return (
      <div className="rounded-2xl border-2 border-dashed border-amber-400/50 bg-amber-400/[0.06] p-6 text-center">
        <div className="w-10 h-10 rounded-full bg-amber-400/15 text-amber-300 flex items-center justify-center mx-auto mb-2">
          <PenLine size={16} />
        </div>
        <div className="label-caps text-amber-300 text-[10px]">Locked · renter-only</div>
        <div className="text-sm text-white mt-1">
          The signature must be drawn by the renter on their own device.
        </div>
        <div className="text-[11px] text-white/50 mt-1">
          Use "Send to renter" above to text + email them the signing link.
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border p-4 ${disabled ? "border-white/8 opacity-60" : "border-gold/40 bg-gold/5"}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="label-caps text-gold text-xs flex items-center gap-1.5">
          <PenLine size={12} /> Digital signature
        </div>
        <button type="button"
          onClick={clear}
          disabled={disabled || !hasInk}
          data-testid={TID.bookRental.signatureClear}
          className="text-[10px] label-caps text-white/60 hover:text-white inline-flex items-center gap-1 disabled:opacity-40">
          <Eraser size={10} /> Clear
        </button>
      </div>
      <div className="rounded-xl border border-white/10 bg-ink-950/70 overflow-hidden">
        <canvas
          ref={canvasRef}
          width={800}
          height={200}
          data-testid={TID.bookRental.signaturePad}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          className={`w-full h-40 ${disabled ? "cursor-not-allowed" : "cursor-crosshair"} touch-none`}
        />
      </div>
      <p className="text-[10px] text-white/40 mt-2">
        {disabled ? "Scroll the agreement above to unlock the signature area." : "Draw your signature above using mouse or finger."}
      </p>
    </div>
  );
}

/* ---------- Step 4: Payment ---------- */
function PaymentStep({ form, setForm, estimate, submitting, onSubmit }) {
  // Cash is intentionally FIRST — most rental customers prefer to settle on
  // the day of pickup. Online methods are grouped after so the cash-first
  // audience finds their option without scrolling.
  const methods = [
    { key: "cash", label: "Cash at Pickup", desc: "Reserve now with $0 due today. Pay the total plus refundable deposit in cash when you pick up the vehicle.", icon: Wallet, online: false, recommended: true },
    { key: "card", label: "Credit / Debit Card", desc: "Secure Stripe checkout — pay full amount now.", icon: CreditCard, online: true },
    { key: "cashapp", label: "Cash App Pay", desc: "Pay instantly through Cash App via Stripe — no manual transfer, no waiting for verification.", icon: DollarSign, online: true },
    { key: "apple_pay", label: "Apple Pay", desc: "Fast one-tap checkout via Stripe on supported devices.", icon: Apple, online: true },
    { key: "paypal", label: "PayPal", desc: "Reserve now — send PayPal payment to concierge before pickup.", icon: DollarSign, online: false },
    { key: "zelle", label: "Zelle", desc: "Reserve now — send Zelle to concierge before pickup.", icon: Banknote, online: false },
  ];
  const selected = methods.find((m) => m.key === form.payment_method);
  return (
    <div className="space-y-5">
      <div className="label-caps text-electric text-xs">Step 4 · Choose payment method</div>
      <div className="grid sm:grid-cols-2 gap-3">
        {methods.map((m) => (
          <button
            key={m.key}
            type="button"
            data-testid={m.key === "card" ? TID.bookRental.payCard
              : m.key === "cash" ? TID.bookRental.payCash
              : `rental-pay-${m.key}`}
            onClick={() => setForm({ ...form, payment_method: m.key })}
            className={`relative text-left rounded-2xl border p-4 transition-all ${
              form.payment_method === m.key
                ? "border-gold bg-gold/10 shadow-[0_0_28px_rgba(249,201,124,0.15)]"
                : "border-white/12 hover:border-white/30"
            }`}
          >
            {m.recommended && (
              <span className="absolute -top-2 right-3 px-2 py-0.5 rounded-full text-[9px] label-caps bg-lime-400/20 border border-lime-400/50 text-lime-300">
                Most popular
              </span>
            )}
            <div className="flex items-center gap-2">
              <m.icon size={18} className={form.payment_method === m.key ? "text-gold" : "text-white/60"} />
              <div className="font-serif text-base">{m.label}</div>
            </div>
            <div className="text-[11px] text-white/60 mt-1">{m.desc}</div>
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-white/8 bg-white/[.02] p-4 text-xs text-white/70 space-y-1">
        <div>Total: <span className="text-gold">${estimate?.total?.toFixed(2) || "—"}</span></div>
        <div>Refundable deposit: <span className="text-white">${estimate?.deposit?.toFixed(2) || "—"}</span> <span className="text-white/40">(refunds within 72 hours of return)</span></div>
        {selected && ["card", "cashapp", "apple_pay"].includes(selected.key) && estimate?.total ? (() => {
          const modes = [
            { key: "full",    label: "Full amount",  amount: estimate.total,                                       desc: "Pay the entire booking now" },
            { key: "deposit", label: "Deposit only", amount: estimate.deposit || 0,                                desc: "Refundable hold, rest at pickup", hide: !(estimate.deposit && estimate.deposit > 0) },
            { key: "half",    label: "50% now",      amount: +(estimate.total / 2).toFixed(2),                     desc: "Split in half — rest due at pickup" },
          ].filter((m) => !m.hide);
          const active = form.payment_amount_mode || "full";
          const activeAmt = modes.find((m) => m.key === active)?.amount || estimate.total;
          const fee = +(activeAmt * 0.029 + 0.30).toFixed(2);
          const totalWithFee = +(activeAmt + fee).toFixed(2);
          return (
            <div data-testid="rental-pay-amount-picker" className="pt-2 mt-2 border-t border-white/8 space-y-2">
              <div className="label-caps text-cyan text-[10px]">How much to pay today</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {modes.map((m) => (
                  <button
                    type="button"
                    key={m.key}
                    data-testid={`rental-pay-mode-${m.key}`}
                    onClick={() => setForm({ ...form, payment_amount_mode: m.key })}
                    className={`text-left rounded-xl border p-2.5 transition ${
                      active === m.key
                        ? "border-cyan bg-cyan/10"
                        : "border-white/12 hover:border-white/30"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] text-white">{m.label}</span>
                      <span className={`text-[11px] ${active === m.key ? "text-cyan font-semibold" : "text-white/60"}`}>${m.amount.toFixed(2)}</span>
                    </div>
                    <div className="text-[10px] text-white/40 mt-0.5">{m.desc}</div>
                  </button>
                ))}
              </div>
              <div
                data-testid={`rental-fee-note-${selected.key}`}
                className="rounded-xl border border-cyan/30 bg-cyan/[0.05] px-3 py-2 text-[11px] text-white/85"
              >
                <span className="label-caps text-cyan text-[10px] mr-1.5">
                  {selected.key === "cashapp" ? "Cash App Pay" : selected.key === "apple_pay" ? "Apple Pay" : "Card"} · charge today
                </span>
                ${activeAmt.toFixed(2)} + ${fee.toFixed(2)} fee (2.9% + $0.30) = <span className="text-cyan font-semibold">${totalWithFee.toFixed(2)}</span>
                {active !== "full" && estimate.total > activeAmt && (
                  <div className="text-white/50 mt-1">
                    Remaining <span className="text-white">${(estimate.total - activeAmt).toFixed(2)}</span> due at pickup.
                  </div>
                )}
              </div>
            </div>
          );
        })() : null}
        <div className="pt-2 mt-2 border-t border-white/8 text-white/60">
          <span className="label-caps text-electric text-[10px] mr-1.5">Final step by Plenty Rydez</span>
          Your rental contract is <span className="text-white">finalized</span> once our team completes a walk-around inspection video of the vehicle. We&apos;ll email you the signed contract with the inspection video attached.
        </div>
      </div>

      {selected && !selected.online && (
        <div className="rounded-xl border border-electric/30 bg-electric/5 p-3 text-xs text-white/80 space-y-1">
          <div className="label-caps text-electric text-[10px]">Concierge payment instructions</div>
          {selected.key === "paypal" && <div>Send <span className="text-gold">${estimate?.total?.toFixed(2) || "—"}</span> via PayPal to <span className="text-white">pay@plenty-rydez.com</span> — note booking # in the memo.</div>}
          {selected.key === "zelle" && <div>Send <span className="text-gold">${estimate?.total?.toFixed(2) || "—"}</span> via Zelle to <span className="text-white">929-899-3467</span> — note booking # in the memo.</div>}
          {selected.key === "cash" && <div>Bring the total <span className="text-gold">${(((estimate?.total || 0) + (estimate?.deposit || 0))).toFixed(2)}</span> — that's <span className="text-white">${estimate?.total?.toFixed(2) || "—"}</span> for the rental plus a <span className="text-white">${estimate?.deposit?.toFixed(2) || "—"}</span> refundable deposit — in cash at pickup. Your ID must match the contract.</div>}
        </div>
      )}

      {/* Mandatory deposit channel when Cash-at-Pickup is selected.
          The refundable deposit MUST land via Apple Pay, Cash App, or Zelle
          before the manager releases the vehicle — that's what secures the
          reservation. Renter is still paying the rental total in cash on
          the day. */}
      {selected?.key === "cash" && (
        <div className="rounded-2xl border border-amber-400/30 bg-amber-400/[.05] p-4 space-y-3">
          <div className="flex items-start gap-2">
            <ShieldAlert size={14} className="text-amber-300 mt-0.5 shrink-0" />
            <div className="text-xs text-white/85">
              <div className="label-caps text-amber-300 text-[10px] mb-1">Required · Refundable deposit</div>
              To hold your reservation we need a <span className="text-gold">${estimate?.deposit?.toFixed(2) || "—"}</span> refundable deposit sent through one of the options below before pickup. The rental total is still paid in cash on the day. The deposit is refunded within 72 hours of a clean return.
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { key: "apple_pay_manual", label: "Apple Pay", icon: Apple },
              { key: "cashapp_manual",   label: "Cash App",  icon: DollarSign },
              { key: "zelle",            label: "Zelle",     icon: Banknote },
            ].map((d) => {
              const active = form.deposit_method === d.key;
              return (
                <button
                  key={d.key}
                  type="button"
                  data-testid={`rental-deposit-${d.key}`}
                  onClick={() => setForm({ ...form, deposit_method: d.key })}
                  className={`rounded-xl border p-3 text-center transition-all ${
                    active
                      ? "border-amber-400 bg-amber-400/10 shadow-[0_0_20px_rgba(251,191,36,0.15)]"
                      : "border-white/12 hover:border-white/30"
                  }`}
                >
                  <d.icon size={16} className={`mx-auto mb-1 ${active ? "text-amber-300" : "text-white/60"}`} />
                  <div className={`text-[11px] label-caps ${active ? "text-amber-200" : "text-white/70"}`}>{d.label}</div>
                </button>
              );
            })}
          </div>
          {form.deposit_method && (
            <div className="text-[11px] text-white/70 leading-relaxed rounded-lg bg-black/25 border border-white/8 p-3">
              {form.deposit_method === "apple_pay_manual" && (
                <>Send <span className="text-gold">${estimate?.deposit?.toFixed(2) || "—"}</span> via Apple Cash / Apple Pay to <span className="text-white">929-899-3467</span>. Include your booking # in the memo. Manager verifies before approving.</>
              )}
              {form.deposit_method === "cashapp_manual" && (
                <>Send <span className="text-gold">${estimate?.deposit?.toFixed(2) || "—"}</span> via Cash App to <span className="text-white">$PlentyRydez</span>. Include your booking # in the memo. Manager verifies before approving.</>
              )}
              {form.deposit_method === "zelle" && (
                <>Send <span className="text-gold">${estimate?.deposit?.toFixed(2) || "—"}</span> via Zelle to <span className="text-white">929-899-3467</span>. Include your booking # in the memo. Manager verifies before approving.</>
              )}
            </div>
          )}
        </div>
      )}

      <button
        data-testid={TID.bookRental.submit}
        disabled={!form.payment_method || submitting || (form.payment_method === "cash" && !form.deposit_method)}
        onClick={() => onSubmit(form.payment_method)}
        className="btn-gold w-full py-3 rounded-full text-sm disabled:opacity-50"
      >
        {submitting
          ? "Processing…"
          : !selected
            ? "Choose a payment method"
            : selected.key === "cash" && !form.deposit_method
              ? "Choose a deposit method above"
              : selected.online
                ? `Proceed to Stripe · $${estimate?.total?.toFixed(2) || "…"}`
                : selected.key === "cash"
                  ? `Confirm reservation · pay ${selected.label} · deposit via ${form.deposit_method?.replace("_manual","").replace("_"," ")}`
                  : `Confirm reservation · pay via ${selected.label} at pickup`}
      </button>
    </div>
  );
}

/* ---------- shared bits ---------- */
function StepBar({ current }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {STEPS.map((s, i) => (
        <div key={s.key} className="flex items-center gap-2">
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-serif border ${
              i < current ? "bg-electric text-ink-900 border-electric"
              : i === current ? "border-gold text-gold" : "border-white/15 text-white/40"
            }`}
          >
            {i < current ? <CheckCircle2 size={14} /> : i + 1}
          </div>
          <span className={`label-caps text-[11px] ${i === current ? "text-gold" : "text-white/40"}`}>{s.label}</span>
          {i < STEPS.length - 1 && <div className="w-6 h-px bg-white/12" />}
        </div>
      ))}
    </div>
  );
}

const FieldRow = ({ label, icon: Icon, children }) => (
  <label className="block">
    <div className="label-caps text-white/60 mb-2 flex items-center gap-1.5">
      {Icon && <Icon size={12} className="text-electric" />}{label}
    </div>
    {children}
  </label>
);

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-white/60">{k}</span>
    <span className="text-white">{v}</span>
  </div>
);

const StyleTag = () => (
  <style>{`
    .input {
      background: #121214; border: 1px solid rgba(255,255,255,0.08);
      color: #fff; border-radius: 12px; padding: 12px 14px;
      width: 100%; font-size: 14px;
      transition: border-color 0.2s;
    }
    .input:focus { outline: none; border-color: #00E5FF; box-shadow: 0 0 0 3px rgba(0,229,255,0.15); }
    .input::-webkit-calendar-picker-indicator { filter: invert(1) opacity(0.6); }
  `}</style>
);

/* --------------------------------------------------------------------
 * StaffSendLinkBanner
 *   Rendered at the top of /book/rental/:vehicleId whenever a staff /
 *   admin user visits the customer-facing rental page. Managers should
 *   NEVER fill out this form themselves — instead they text/email the
 *   pre-filled link to the actual renter so the customer completes the
 *   4-page contract + DL upload + signature on their own phone. Manager
 *   just approves the signed rental in the Rentals tab afterwards.
 * -------------------------------------------------------------------- */
function StaffSendLinkBanner({ vehicle, form, startISO, endISO }) {
  const [phone, setPhone] = React.useState(form?.phone || "");
  const [email, setEmail] = React.useState(form?.email || "");
  const [name, setName] = React.useState(form?.full_name || "");
  const [sending, setSending] = React.useState(false);
  const [draftUrl, setDraftUrl] = React.useState("");
  React.useEffect(() => { if (form?.full_name) setName(form.full_name); }, [form?.full_name]);
  React.useEffect(() => { if (form?.email) setEmail(form.email); }, [form?.email]);
  React.useEffect(() => { if (form?.phone) setPhone(form.phone); }, [form?.phone]);

  const shareUrl = React.useMemo(() => {
    const base = window.location.origin;
    const params = new URLSearchParams();
    if (name.trim()) params.set("prefill_name", name.trim());
    if (email.trim()) params.set("prefill_email", email.trim());
    if (phone.trim()) params.set("prefill_phone", phone.trim());
    if (startISO) params.set("prefill_start", startISO);
    if (endISO) params.set("prefill_end", endISO);
    if (form?.pickup_location) params.set("prefill_pickup", form.pickup_location);
    if (form?.return_location) params.set("prefill_return", form.return_location);
    const qs = params.toString();
    return `${base}/book/rental/${vehicle.id}${qs ? `?${qs}` : ""}`;
  }, [vehicle.id, name, email, phone, startISO, endISO, form?.pickup_location, form?.return_location]);

  const copyLink = async (u) => {
    try {
      await navigator.clipboard.writeText(u || shareUrl);
      toast.success("Rental link copied");
    } catch { toast.error("Copy failed"); }
  };

  const approveAndSend = async () => {
    if (!phone.trim() && !email.trim()) {
      return toast.error("Add the renter's phone or email");
    }
    if (!name.trim()) {
      return toast.error("Add the renter's name first");
    }
    setSending(true);
    try {
      // Snapshot the entire form state so the customer resumes exactly
      // where the manager left off. Signature / initials / DL files are
      // stripped so the renter must add those themselves.
      const snapshot = { ...form };
      ["signature_data","dl_front_file","dl_back_file","dl_front_url","dl_back_url",
       "accepts_terms","contract_initials_p1","contract_initials_p2",
       "contract_initials_p3","contract_initials_p4"].forEach((k) => { delete snapshot[k]; });
      const { data } = await api.post("/admin/rental-drafts", {
        vehicle_id: vehicle.id,
        full_name: name.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
        form_data: snapshot,
        send_now: true,
      });
      setDraftUrl(data.url);
      const parts = [];
      if (data.sms_sent) parts.push("SMS");
      if (data.email_sent) parts.push("email");
      if (parts.length) toast.success(`Approved — resume link sent via ${parts.join(" + ")}`);
      else toast.warning("Draft saved but SMS/email delivery failed. Copy the link and send it manually.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    } finally {
      setSending(false);
    }
  };

  const sendLink = async () => {
    if (!phone.trim() && !email.trim()) {
      return toast.error("Add the renter's phone or email");
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/rental-links/send", {
        vehicle_id: vehicle.id,
        full_name: name.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        start_at: startISO || null,
        end_at: endISO || null,
        pickup_location: form?.pickup_location || null,
        return_location: form?.return_location || null,
      });
      const parts = [];
      if (data.sms_sent) parts.push("SMS");
      if (data.email_sent) parts.push("email");
      if (parts.length) toast.success(`Rental link sent via ${parts.join(" + ")}`);
      else toast.warning("Link generated — but delivery failed. Copy the link and paste it manually.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="staff-send-link-banner"
      className="mb-6 rounded-2xl border border-cyan/40 bg-cyan/[0.06] p-5"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-cyan/20 text-cyan flex items-center justify-center flex-shrink-0">
          <ShieldAlert size={18} />
        </div>
        <div className="flex-1">
          <div className="label-caps text-cyan text-[10px]">Staff view · not for manager use</div>
          <div className="text-sm text-white leading-tight mt-1">
            You're signed in as a manager. <strong>The renter</strong> should fill out this form and upload their driver's license from <strong>their own phone</strong>. Send them the rental link below — you approve in the Rentals tab once they sign.
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-2 mb-3">
        <input
          data-testid="staff-send-link-name"
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Renter's name (optional)"
          className="input" />
        <input
          data-testid="staff-send-link-phone"
          value={phone} onChange={(e) => setPhone(e.target.value)}
          placeholder="Renter's phone (SMS)"
          className="input" />
        <input
          data-testid="staff-send-link-email"
          value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="Renter's email"
          className="input" />
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <button
          type="button"
          onClick={approveAndSend}
          disabled={sending}
          data-testid="staff-approve-send-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs label-caps bg-lime-400 text-ink-900 font-semibold hover:brightness-110 disabled:opacity-50"
        >
          <CheckCircle2 size={12} /> {sending ? "Sending…" : "Approve & send resume link"}
        </button>
        <button
          type="button"
          onClick={sendLink}
          disabled={sending}
          data-testid="staff-send-link-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs label-caps text-cyan border border-cyan/40 bg-cyan/10 hover:bg-cyan/15"
        >
          <ArrowRight size={12} /> Quick share (no save)
        </button>
        <button
          type="button"
          onClick={() => copyLink(draftUrl || shareUrl)}
          data-testid="staff-copy-link-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs label-caps text-cyan border border-cyan/40 bg-cyan/10 hover:bg-cyan/15"
        >
          <Copy size={12} /> Copy link
        </button>
        <div className="text-[10px] text-white/40 font-mono truncate flex-1 min-w-[120px]">
          {draftUrl || shareUrl}
        </div>
      </div>
      {draftUrl && (
        <div
          data-testid="staff-draft-url"
          className="mt-3 rounded-xl border border-lime-400/40 bg-lime-400/[0.06] p-3 text-[11px] text-lime-200"
        >
          <div className="label-caps text-lime-300 text-[10px] mb-1">Draft saved · renter link active</div>
          The renter can open this link to resume the contract at DL upload + signature.
          The link expires in 14 days and stops working once they submit.
        </div>
      )}
    </div>
  );
}

